import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { format } from "date-fns";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { listClientsheetOverviewsApiV1ClientsheetOverviewGet, listClientsheetsApiV1ClientsheetGet } from "@/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, type ChartConfig } from "@/components/ui/chart";
import { CardStatsSection } from "@/components/CardStats";
import { DownloadClientsheetButton } from "@/components/ClientsheetTable/DownloadClientsheetButton";
import { DateRangePicker } from "@/components/DateRangePicker";
import { LoadingComponent } from "@/components/LoadingComponent";
import { checkPermission } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/clientsheets/")({
  component: ClientsheetsPage,
  beforeLoad: () => {
    checkPermission("clientsheet:read");
  },
});

function ClientsheetsPage() {
  const { t } = useTranslation();
  const [date, setDate] = useState<{ from: Date; to: Date }>({
    from: new Date(2025, 0, 1),
    to: new Date(),
  });

  const { data: clientsheetsData, isLoading: isClientsheetsLoading } = useQuery({
    queryKey: ["clientsheets"],
    queryFn: async () => {
      const response = await listClientsheetsApiV1ClientsheetGet();
      return response.data;
    },
  });

  const { data: clientsheetOverviews, isLoading: isOverviewsLoading } = useQuery({
    queryKey: ["clientsheetOverviews"],
    queryFn: async () => {
      const response = await listClientsheetOverviewsApiV1ClientsheetOverviewGet();
      return response.data;
    },
  });

  if (isClientsheetsLoading || isOverviewsLoading) {
    return <LoadingComponent />;
  }

  const maybeLatestClientsheet =
    clientsheetsData?.length && clientsheetsData.length > 0
      ? clientsheetsData?.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0]
      : null;

  const chartData =
    clientsheetOverviews
      ?.filter((item) => {
        const itemDate = new Date(item.date);
        return itemDate >= date.from && itemDate <= date.to;
      })
      ?.map((item) => ({
        timestamp: new Date(item.date).getTime(),
        margin: item.margin_amount,
        delivered: item.delivered_amount,
        invoiced: item.invoiced_amount,
      })) || [];

  const chartConfig = {
    margin: {
      label: "Margin",
      color: "hsl(var(--chart-1))",
    },
    delivered: {
      label: "Delivered",
      color: "hsl(var(--chart-2))",
    },
    invoiced: {
      label: "Invoiced",
      color: "hsl(var(--chart-3))",
    },
  } satisfies ChartConfig;

  return (
    <div className="flex h-full flex-col gap-4">
      {clientsheetOverviews && clientsheetOverviews.length > 0 && <CardStatsSection data={clientsheetOverviews} />}

      <div className="flex w-full flex-col-reverse items-center gap-2 sm:flex-col-reverse md:flex-row md:justify-between">
        {clientsheetOverviews && clientsheetOverviews.length > 0 && (
          <DateRangePicker date={date} setDate={setDate} className="w-full md:w-auto" />
        )}

        {maybeLatestClientsheet && (
          <div className="flex w-full flex-col items-center gap-2 sm:flex-row md:w-auto">
            <DownloadClientsheetButton
              date={maybeLatestClientsheet.date}
              text="Download Latest Clientsheet"
              className="w-full sm:w-auto"
            />
            <Link
              to="/clientsheets/archive"
              className="w-full rounded-md bg-muted px-4 py-2 text-center hover:bg-muted/80 sm:w-auto"
            >
              {t("Browse Archive")}
            </Link>
          </div>
        )}
      </div>

      {chartData.length > 0 && (
        <div>
          <Card>
            <CardHeader>
              <CardTitle>
                {t("Clientsheets Overview")} ({format(date.from, "yyyy-MM-dd")} - {format(date.to, "yyyy-MM-dd")})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-48 w-full lg:h-[30rem]">
                <LineChart
                  data={chartData.reverse()}
                  margin={{
                    top: 5,
                    right: 10,
                    left: 10,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="timestamp"
                    tickFormatter={(unixTime) => new Date(unixTime).toISOString().split("T")[0]}
                    tickLine={false}
                    axisLine={false}
                    tickMargin={2}
                  />
                  <YAxis />
                  <ChartTooltip
                    labelFormatter={(v) => new Date(v).toISOString().split("T")[0]}
                    formatter={(value) => Number(value).toFixed(2)}
                    labelStyle={{ color: "GrayText" }}
                  />
                  <Line dataKey="margin" type="monotone" stroke="var(--color-margin)" strokeWidth={2} dot={false} />
                  <Line
                    dataKey="delivered"
                    type="monotone"
                    stroke="var(--color-delivered)"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line dataKey="invoiced" type="monotone" stroke="var(--color-invoiced)" strokeWidth={2} dot={false} />
                </LineChart>
              </ChartContainer>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
